/**
 * `lib/blockDtoWire` — the ONE decoder for the server's `body_dto_b64` channel.
 *
 * The backend ships the docz block-DTO list wire base64-encoded on two routes
 * (`GET /api/documents/:id/docz` and `.../branded-docz`, both in
 * `src/handlers/doc_content.zig`). Two call sites consumed it and each had
 * hand-rolled its own `atob` loop; this is that primitive, defined once.
 *
 * RULE ZERO: the BYTES are decoded by `@norkron/web-docz`'s own
 * `decodeBlockList` — there is no second block-DTO reader in this app and there
 * must never be one. This module is only the base64 envelope around it.
 *
 * UPSTREAM NOTE (RULE 13): the base64 form of the wire is arguably web-docz's
 * own concern — a `decodeBlockListBase64` there would delete this file. Filed as
 * `.claude/plans/feature-requests/FR-web-docz-decode-block-list-base64.md`
 * rather than shipped inline, because web-docz's `dist/` is the artefact every
 * concurrent elaborate worktree resolves through a shared symlink and rebuilding
 * it mid-flight would change the build under other agents.
 */

/**
 * Decode a standard-base64 `body_dto_b64` payload to the block-DTO list bytes.
 * `atob` yields a binary string; each char code is one byte.
 *
 * Throws (`InvalidCharacterError`) on a non-base64 input — callers DEGRADE to
 * the plain-text channel rather than blanking the document.
 */
export function base64ToBlockDtoBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}
